/**
 * MainDashPage — V2 main dashboard with stat cards, top-10 tables,
 * charts (trend, pie, hourly), and area shift breakdown.
 *
 * Fixed 28-day window. Refreshes every 10 minutes. Area type filter drawer.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';

/* ── DashboardInfoTip — ⓘ tooltip next to page title ── */
const DASH_TOOLTIP = [
    'Dashboard data covers the previous 28 days (4 weeks) and includes',
    'Pendant and Pull Cord device events with a response time under 60 minutes.',
];

const DashboardInfoTip = () => (
    <HeaderInfoTip spaced title="About This Dashboard" body={DASH_TOOLTIP.join(' ')} />
);
import {
    getSession, getCampusId, getCampusList, updateSessionCampus, getCampusConfig,
} from '../../../services/session.js';
import { formatDateIso, getToday } from '../../../libs.jsx';
import {
    fetchDashboardStats, fetchResidentSummary, fetchCaregiverSummary,
    fetchAreaSummary, fetchAreaShiftSummary, fetchReasonData,
    fetchHourChartData, fetchDashboardFilterOptions,
} from './api/mainDashApi.js';

import StatCards from './components/StatCards.jsx';
import TopResidentsTable from './components/TopResidentsTable.jsx';
import TopCaregiversTable from './components/TopCaregiversTable.jsx';
import ResponseTrendChart from './components/ResponseTrendChart.jsx';
import ReasonPieChart from './components/ReasonPieChart.jsx';
import AreaShiftTable from './components/AreaShiftTable.jsx';
import HourlyChart from './components/HourlyChart.jsx';
import ResidentTrendEpisodes from '../../../components/resident-trends/ResidentTrendEpisodes.jsx';

import './main-dash-page.css';
import Drawer from '../../../components/v2/Drawer.jsx';
import HeaderInfoTip from '../../../components/v2/HeaderInfoTip.jsx';

const MainDashPage = () => {
    // ======================================================================
    // SESSION
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    // Grandstream campuses get Time to Connect on five widgets (docs/TIME_TO_CONNECT_PLAN.md,
    // phase 3). The procs compute it for every campus but return NULL off Grandstream.
    const isGrandstream = Boolean(campusConfig?.is_grandstream);

    // Fixed 28-day window
    const startDate = useMemo(() => formatDateIso(getToday(-28)), []);
    const endDate = useMemo(() => formatDateIso(getToday()), []);

    // ======================================================================
    // DATA STATE
    // ======================================================================
    const [statData, setStatData] = useState({});
    const [residentData, setResidentData] = useState([]);
    const [caregiverData, setCaregiverData] = useState([]);
    const [areaData, setAreaData] = useState([]);
    const [areaShiftData, setAreaShiftData] = useState([]);
    const [reasonData, setReasonData] = useState({});
    const [hourChartData, setHourChartData] = useState({});
    const [loading, setLoading] = useState(true);
    const hasLoadedRef = useRef(false);

    // ======================================================================
    // FILTER STATE
    // ======================================================================
    const [areaTypeOptions, setAreaTypeOptions] = useState([]);
    const [selectedAreaTypes, setSelectedAreaTypes] = useState([]);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [filterSearch, setFilterSearch] = useState('');

    // ======================================================================
    // TOAST
    // ======================================================================
    const { toast, showToast, dismissToast } = useToast();

    // ======================================================================
    // LOAD FILTER OPTIONS
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        fetchDashboardFilterOptions(campusId).then((types) => {
            setAreaTypeOptions(Array.isArray(types) ? types : []);
        });
    }, [campusId]);

    // ======================================================================
    // LOAD DASHBOARD DATA
    // ======================================================================
    const areaTypeFilter = useMemo(() => selectedAreaTypes.join(','), [selectedAreaTypes]);

    const loadData = useCallback(async () => {
        if (!campusId) return;
        try {
            const [stats, residents, caregivers, areas, shifts, reasons, hourly] = await Promise.all([
                fetchDashboardStats(campusId, startDate, endDate, areaTypeFilter),
                fetchResidentSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchCaregiverSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchAreaSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchAreaShiftSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchReasonData(campusId, startDate, endDate, areaTypeFilter),
                fetchHourChartData(campusId, startDate, endDate, areaTypeFilter),
            ]);
            console.log(residents)
            // Stats comes as array — take first element
            setStatData(Array.isArray(stats) && stats.length > 0 ? stats[0] : stats || {});
            // Residents, caregivers, areas, shifts come as { records: [...], totals: [...] }
            setResidentData(residents?.records || []);
            setCaregiverData(caregivers?.records || []);
            setAreaData(areas?.records || []);
            setAreaShiftData(shifts?.records || []);
            setReasonData(reasons || {});
            setHourChartData(hourly || {});
        } catch (err) {
            console.error('[MainDashPage] loadData error:', err);
            showToast('error', 'Failed to load dashboard data');
        } finally {
            if (!hasLoadedRef.current) {
                hasLoadedRef.current = true;
                setLoading(false);
            }
        }
    }, [campusId, startDate, endDate, areaTypeFilter, showToast]);

    useEffect(() => { loadData(); }, [loadData]);

    // Polling: 600s (10 minutes)
    useEffect(() => {
        const timer = setInterval(loadData, 600000);
        return () => clearInterval(timer);
    }, [loadData]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setSelectedAreaTypes([]);
    }, []);

    const handleToggleAreaType = useCallback((areaTypeId) => {
        setSelectedAreaTypes((prev) => {
            const id = String(areaTypeId);
            return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        });
    }, []);

    const handleSelectAllAreaTypes = useCallback(() => {
        setSelectedAreaTypes(areaTypeOptions.map((at) => String(at.area_type_id)));
    }, [areaTypeOptions]);

    const handleClearAreaTypes = useCallback(() => {
        setSelectedAreaTypes([]);
    }, []);

    const handleApplyFilter = useCallback(() => {
        setFilterDrawerOpen(false);
    }, []);

    const filteredAreaTypeOptions = useMemo(() => {
        if (!filterSearch) return areaTypeOptions;
        const term = filterSearch.toLowerCase();
        return areaTypeOptions.filter((at) => (at.area_type_name || '').toLowerCase().includes(term));
    }, [areaTypeOptions, filterSearch]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="md-page">
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Page Header */}
                    <header className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <nav className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Dashboards</span>
                                    <span className="ps-breadcrumb__separator">/</span>
                                    <span className="ps-breadcrumb__current">Main</span>
                                </nav>
                                <div className="ps-campus-row">
                                    <label htmlFor="md-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="md-campus-selector" className="ps-campus-select" value={campusId} onChange={handleCampusChange}>
                                            {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-2)' }}>
                                <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-heading)', margin: 0 }}>Dashboard<DashboardInfoTip /></h1>
                                <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setFilterDrawerOpen(true)} style={{ position: 'relative' }}>
                                    <i className="fe fe-filter" style={{ marginRight: '4px' }} />
                                    Filter
                                    {selectedAreaTypes.length > 0 ? (
                                        <span className="ar-filter-badge">{selectedAreaTypes.length}</span>
                                    ) : null}
                                </button>
                            </div>
                        </div>
                    </header>

                    {/* Content */}
                    <div className="md-content">
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>
                                <div className="ar-loading__spinner" style={{ margin: '0 auto var(--space-3)' }} />
                                <p>Loading dashboard...</p>
                            </div>
                        ) : null}

                        {!loading ? (
                            <>
                        {/* Stat Cards */}
                        <StatCards statData={statData} loading={loading} isGrandstream={isGrandstream} />

                        {/* Top 10 Tables (side by side) */}
                        <div className="md-two-col">
                            <TopResidentsTable data={residentData} />
                            <TopCaregiversTable data={caregiverData} isGrandstream={isGrandstream} />
                        </div>

                        {/* Charts (side by side) */}
                        <div className="md-two-col">
                            <ResponseTrendChart areaData={areaData} isGrandstream={isGrandstream} />
                            <ReasonPieChart reasonData={reasonData} />
                        </div>

                        {/* Area Shift Table (full width) */}
                        <AreaShiftTable data={areaShiftData} hourChartData={hourChartData} isGrandstream={isGrandstream} />

                        {/* Hourly Chart (full width) */}
                        <HourlyChart chartData={hourChartData} avgResponseTime={statData?.avg_response_time} isGrandstream={isGrandstream} />

                        {/* Resident Trends & Actions (conditional on campus feature flag) */}
                        {campusConfig?.resident_trends ? (
                            <ResidentTrendEpisodes campusId={campusId} />
                        ) : null}
                            </>
                        ) : null}
                    </div>

                    {/* Filter Drawer (area types only) */}
                    <Drawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        title="Filter by Area Type"
                        size="lg"
                        bodyProps={{ style: { overflowY: 'auto', flex: 1 } }}
                        footer={(
                            <>
                                <button className="dp-btn dp-btn--cancel" onClick={handleClearAreaTypes}>Clear All</button>
                                <button className="dp-btn dp-btn--primary" onClick={handleApplyFilter}>Apply Filter</button>
                            </>
                        )}
                    >
                        <input
                            type="text"
                            className="md-filter-search"
                            placeholder="Search area types..."
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                        />
                        <div className="md-filter-actions">
                            <button className="rp-btn rp-btn--secondary" onClick={handleSelectAllAreaTypes} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>Select All</button>
                            <button className="rp-btn rp-btn--secondary" onClick={handleClearAreaTypes} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>Clear</button>
                        </div>
                        <div className="md-filter-list">
                            {filteredAreaTypeOptions.map((at) => {
                                const id = String(at.area_type_id);
                                return (
                                    <label key={id} className="md-filter-item">
                                        <input type="checkbox" checked={selectedAreaTypes.includes(id)} onChange={() => handleToggleAreaType(id)} />
                                        <span>{at.area_type_name || at.area_type_description || '(unknown)'}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </Drawer>
                </div>
            </div>
        </>
    );
};

export default MainDashPage;
